// Change WHERE an assigned role applies and WHEN it ends (PATCH
// rbac/assignments/:id). Backend refusals (scope beyond your own reach, your
// own scope, unknown company) are toasted verbatim.
//
// F5: a "where" change that makes the person LOSE companies asks first, listing
// what they lose (useConfirmScopeImpact). Widening and end-date-only changes
// never ask. The first-activation 409 gate still runs afterwards, unchanged.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import type { LegalEntityLite } from '@/features/payroll/types';
import { updateAssignmentScope, type Assignment } from '@/services/rbacService';
import { AssignmentScopeFields } from './AssignmentScopeFields';
import { useSaveWithLockoutConfirm } from './lockoutConfirm';
import { useConfirmScopeImpact } from './scopeImpactConfirm';
import {
  formFromAssignment,
  hasErrors,
  scopeChanged,
  toUpdatePayload,
  validateScopeForm,
  type ScopeForm,
} from './assignmentScope';

interface Props {
  assignment: Assignment | null;
  roleLabel: string;
  personName: string;
  entities: LegalEntityLite[];
  entitiesError?: string | null;
  /** Company name for an id (defaults to the `entities` list). */
  entityName?: (id: string) => string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export const EditAssignmentScopeDialog: React.FC<Props> = ({
  assignment,
  roleLabel,
  personName,
  entities,
  entitiesError,
  entityName,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<ScopeForm | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveWithLockoutConfirm = useSaveWithLockoutConfirm();
  const confirmScopeImpact = useConfirmScopeImpact();
  const nameOf = useMemo(
    () =>
      entityName ?? ((id: string) => entities.find((e) => e.id === id)?.name ?? `Company ${id.slice(0, 8)}`),
    [entityName, entities],
  );

  useEffect(() => {
    setForm(assignment ? formFromAssignment(assignment) : null);
    setTried(false);
  }, [assignment]);

  const errors = useMemo(() => {
    if (!form) return {};
    // A legacy all-companies assignment left on "all" keeps its scope (no names needed).
    const keepsLegacyAll = form.where === 'all' && assignment?.entityScope === 'ALL_AUTHORIZED_ENTITIES';
    return validateScopeForm(form, undefined, keepsLegacyAll ? undefined : entities.map((e) => e.id));
  }, [form, entities, assignment]);

  const save = async () => {
    if (!assignment || !form) return;
    setTried(true);
    if (hasErrors(errors)) return;
    setSaving(true);
    try {
      const payload = toUpdatePayload(form, entities.map((e) => e.id), assignment.entityScope ?? null);
      // Narrowing removes companies from this person: show what they lose first.
      if (scopeChanged(assignment, form)) {
        const go = await confirmScopeImpact({
          assignmentId: assignment.id,
          scope: payload,
          personName,
          roleLabel,
          entityName: nameOf,
        });
        if (!go) return;
      }
      // Scoping the first assignment can lock people out of payroll: confirm first.
      const result = await saveWithLockoutConfirm((ack) =>
        updateAssignmentScope(assignment.id, ack ? { ...payload, acknowledgeLockout: true } : payload),
      );
      if (!result.saved) return;
      toast.success(`Updated where “${roleLabel}” applies for ${personName}`);
      await onSaved();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update this role’s scope'), { duration: 7000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!assignment} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Where “{roleLabel}” applies</DialogTitle>
          <DialogDescription>
            For {personName}. The role decides what they can do; this decides in which companies and until when.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <AssignmentScopeFields
            idPrefix="edit-scope"
            value={form}
            onChange={setForm}
            errors={errors}
            showErrors={tried}
            entities={entities}
            entitiesError={entitiesError}
            disabled={saving}
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAssignmentScopeDialog;
